// The characters of a cartoon: what kinds there are, what each looks like, and how a cast gets its colours. Pure data and small functions - the drawing is in toon2d.js / toon3d.js.

export const KINDS = {
  man: { label: "Man", species: "human", scale: 1, head: 1, voice: "male" },
  woman: { label: "Woman", species: "human", scale: 0.97, head: 1, voice: "female" },
  boy: { label: "Boy", species: "human", scale: 0.64, head: 1.32, voice: "child" },
  girl: { label: "Girl", species: "human", scale: 0.62, head: 1.32, voice: "child" },
  grandpa: { label: "Grandfather", species: "human", scale: 0.93, head: 1, voice: "male" },
  grandma: { label: "Grandmother", species: "human", scale: 0.9, head: 1, voice: "female" },
  cat: { label: "Cat", species: "animal", scale: 0.78, head: 1.3, voice: "female" },
  dog: { label: "Dog", species: "animal", scale: 0.88, head: 1.22, voice: "male" },
  rabbit: { label: "Rabbit", species: "animal", scale: 0.76, head: 1.25, voice: "child" },
  lion: { label: "Lion", species: "animal", scale: 1.08, head: 1.2, voice: "male" },
  mouse: { label: "Mouse", species: "animal", scale: 0.5, head: 1.35, voice: "child" },
  bear: { label: "Bear", species: "animal", scale: 1.12, head: 1.15, voice: "male" },
  bird: { label: "Bird", species: "animal", scale: 0.62, head: 1.3, voice: "child" },
};
export const KIND_IDS = Object.keys(KINDS);
export const HUMANS = KIND_IDS.filter((k) => KINDS[k].species === "human");
export const ANIMALS = KIND_IDS.filter((k) => KINDS[k].species === "animal");

export const SKINS = ["#f1c6a2", "#e0ac86", "#c68d64", "#8d5a3b", "#f6d6bf"];
export const HAIRS = ["#2a1a12", "#5b3a22", "#15110e", "#d7b068", "#a8431f", "#7b7f86"];
export const TOPS = ["#e2574c", "#3a8dde", "#4caf6e", "#f2b632", "#9b59d0", "#ef7fa5", "#2fb5b0", "#f08a3c"];
export const BOTTOMS = ["#2b3a67", "#4a4f57", "#5b3d2b", "#2d6a5a", "#7a2f4b"];
export const FURS = {
  cat: ["#e8a55b", "#9aa0a8", "#3b3b44", "#f2f2f2"], dog: ["#b9854f", "#e9d6b0", "#5a3d2b", "#d9843a"], rabbit: ["#f4f4f4", "#c9b79c", "#b6b6c4"], lion: ["#e0a040"],
  mouse: ["#b8b8c2", "#d8c8b0"], bear: ["#7a5237", "#3d2f28", "#e8e8e8"], bird: ["#f2c230", "#e2574c", "#4aa3df", "#7bc96f"],
};
export const HAIR_STYLES = { man: ["short", "slick", "side"], woman: ["long", "bob", "bun"], boy: ["short", "tuft"], girl: ["pigtails", "bob"], grandpa: ["bald", "short"], grandma: ["bun", "bob"] };

// A look for the cast member number `index` of a kind: colours spread so that two people of one kind do not look alike.
export function defaultLook(kind, index = 0) {
  const info = KINDS[kind] || KINDS.man;
  const pick = (list, shift = 0) => list[(index * 3 + shift) % list.length];
  if (info.species === "animal") {
    const fur = pick(FURS[kind] || FURS.cat);
    return { fur, top: pick(TOPS, 1), bottom: pick(BOTTOMS), accent: kind === "lion" ? "#8a4b17" : "#f7e3c4" };
  }
  const styles = HAIR_STYLES[kind] || ["short"];
  const aged = kind === "grandpa" || kind === "grandma";
  return { skin: pick(SKINS, 1), hair: aged ? "#c9cdd2" : pick(HAIRS), hairStyle: styles[index % styles.length], top: pick(TOPS, 2), bottom: pick(BOTTOMS, 1), shoes: "#3a2f2a" };
}

// A cast member as the page and the renderers use it: { id, name, kind, ...look }. `raw` may lack anything but the kind.
export function castMember(raw, index = 0) {
  if (raw.kind === "narrator") return { ...raw, id: raw.id || `c${index + 1}`, name: raw.name || "Narrator", kind: "narrator", index };   // a voice without a body
  const kind = KINDS[raw.kind] ? raw.kind : "man";
  return { ...defaultLook(kind, index), ...raw, id: raw.id || `c${index + 1}`, name: raw.name || KINDS[kind].label, kind, index };
}

export const SETTINGS = {
  park: { label: "Park", sky: ["#8fd3ff", "#d8f1ff"], ground: "#6fbf5a", indoor: false },
  forest: { label: "Forest", sky: ["#6fb7a2", "#cdeedd"], ground: "#4f9a49", indoor: false },
  village: { label: "Village", sky: ["#92cff2", "#e6f6ff"], ground: "#78b95c", indoor: false },
  home: { label: "Home", sky: ["#f2e3c9", "#f2e3c9"], ground: "#b98b5e", indoor: true },
  street: { label: "Street", sky: ["#9ac6e8", "#e8f2fa"], ground: "#6f7480", indoor: false },
  school: { label: "School", sky: ["#e7efe7", "#e7efe7"], ground: "#c8a77a", indoor: true },
  farm: { label: "Farm", sky: ["#8fd0f3", "#fdf3d6"], ground: "#8cc152", indoor: false },
  beach: { label: "Beach", sky: ["#7fd0f5", "#e6f7ff"], ground: "#f0d9a0", indoor: false },
  night: { label: "Night", sky: ["#101a3d", "#2d3a73"], ground: "#2c4a3a", indoor: false },
  mountain: { label: "Mountains", sky: ["#8ec5f0", "#eaf5ff"], ground: "#8fa39a", indoor: false },
};
export const SETTING_IDS = Object.keys(SETTINGS);
